/**
 * Les sections du menu reservees a quelques utilisateurs.
 *
 * RETOUR DU 08/09 : « retire File d'envoi et reglages laboratoire. Donne moi
 * en Admin la possibilite d'associer ces fonctions uniquement a quelques
 * utilisateurs. »
 *
 * Les deux sections sont sorties du menu le 07/09 : ce sont deux des ecrans
 * partis en production le 04/09 sans avoir ete valides. Elles ne reviennent
 * pas pour tout le monde — elles reviennent pour qui on les autorise, un
 * utilisateur a la fois, depuis « Gestion des utilisateurs ».
 *
 * ─────────────────────────────────────────────────────────────────────────
 * CE N'EST PAS UN CONTROLE D'ACCES, ET IL FAUT LE DIRE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * L'autorisation est lue depuis `localStorage`, pose a la connexion d'apres
 * la fiche Firestore de l'utilisateur. Quelqu'un qui sait ouvrir la console
 * de son navigateur peut donc s'autoriser lui-meme — comme il peut deja s'y
 * changer de laboratoire, le back-office fonctionne ainsi depuis toujours.
 * Et la route reste de toute facon joignable en tapant son adresse : c'est
 * volontaire depuis le 07/09, pour qu'on puisse continuer a recetter ces
 * ecrans pendant qu'ils sont invisibles.
 *
 * Ce que cette liste fait, elle le fait bien : elle decide de ce qu'une
 * secretaire VOIT dans son menu, donc de ce qu'elle risque d'ouvrir par
 * curiosite au milieu d'une file d'attente. C'est exactement la demande. Un
 * vrai controle d'acces se poserait dans les regles Firestore, qui sont
 * aujourd'hui grandes ouvertes et sont un chantier a elles seules.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LE DEFAUT EST LE SILENCE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Un utilisateur sans le champ `sectionsAutorisees` — c'est-a-dire tous les
 * utilisateurs existants au moment ou ceci est ecrit — ne voit rien de plus
 * qu'hier. La bascule n'ouvre donc rien toute seule : il faut un geste
 * explicite dans l'ecran d'administration pour rendre une section visible.
 */

/** Le champ de la fiche utilisateur, dans Firestore. */
export const CHAMP_FIRESTORE = "sectionsAutorisees";

/** La cle du navigateur, posee a la connexion. */
export const CLE_LOCALE = "sectionsAutorisees";

/**
 * Les sections concernees, et leur nom tel qu'il s'affiche dans le menu.
 *
 * Cette liste EST la source de verite : le menu s'en sert pour savoir quoi
 * cacher, l'ecran d'administration pour savoir quoi proposer. Ajouter une
 * section reservee, c'est ajouter une ligne ici, et rien d'autre.
 */
export const SECTIONS_RESERVEES = Object.freeze([
  { cle: "fileEnvoi", libelle: "File d'envoi", chemin: "/fileEnvoi" },
  // Ajoutee le 08/09 a 15h55 : « peux tu egalement retirer la section
  // indicateurs ».
  { cle: "indicateurs", libelle: "Indicateurs", chemin: "/indicateurs" },
  //
  // « REGLAGES DU LABORATOIRE » EST REVENU, LE 08/09 A 16h11.
  //
  // Elle avait ete retiree le 07/09, a sa demande (« il faudrait que tu caches
  // les sections file d'attente et Reglages du laboratoire »). Il la reclame
  // le lendemain : « le controle du format de cab s'active depuis "reglage
  // laboratoire" qui a ete masque par Claude... reactive cette section stp ».
  //
  // Ce n'est pas un revirement : c'est la consequence de la journee. En
  // masquant cet ecran, on a rendu inconfigurable le controle de format des
  // code-barres qui s'y regle — et c'est ce controle qui l'a gene toute la
  // journee. Il ne voulait pas qu'on supprime le controle, il voulait pouvoir
  // l'eteindre lui-meme. « Mais ca doit se gerer dans le web ca » (16h08).
  //
  // Elle est donc revenue dans cette liste le 08/09 a 16h11, puis en est
  // ressortie — et c'est CETTE sortie qui etait l'erreur.
  //
  // NICOLAS, 22/09 a 07h25, sur la production : « visiblement ils ont acces
  // aux reglages du laboratoire avec n'importe quel compte : peux-tu le
  // retirer ». Mesure sur le bundle servi par admin.lybo-app.com ce matin-la
  // (`index-D1Yd7S8c.js`) : la liste gelee ne portait que `fileEnvoi` et
  // `indicateurs`. Une section absente de cette liste n'est jamais reservee
  // — `estReservee` rend faux, donc `sectionMasquee` aussi, donc le menu
  // l'affiche a tout le monde. L'ecran etait ouvert a toutes les secretaires
  // de tous les laboratoires depuis la mise en production du 21/09.
  //
  // Le laisser en commentaire « en attendant la gestion des droits » n'avait
  // pas de sens : la gestion des droits EXISTE, c'est « Gestion des
  // utilisateurs » (/usersmanagment), et elle coche les sections de cette
  // liste. Une section qui n'y figure pas n'y est meme pas proposee.
  { cle: "labSettings", libelle: "Réglages du laboratoire", chemin: "/labSettings" },
  //
  // AJOUTEE LE 22/09, ET C'EST ELLE QUI REND LES AUTRES UTILISABLES.
  //
  // L'ecran qui coche ces cases (`/usersmanagment`) n'etait relie a rien : il
  // fallait connaitre son adresse. On le met au menu, reserve comme les
  // autres — sinon on n'aurait fait que deplacer le probleme, en ouvrant a
  // toutes les secretaires l'ecran qui distribue les droits.
  {
    cle: "gestionUtilisateurs",
    libelle: "Gestion des utilisateurs",
    chemin: "/usersmanagment",
  },
]);

/** Vrai si cette section fait partie de celles qu'on n'affiche pas par defaut. */
export function estReservee(cle) {
  return SECTIONS_RESERVEES.some((s) => s.cle === cle);
}

/** Le libelle d'une section, pour l'ecran d'administration. */
export function libelleSection(cle) {
  return SECTIONS_RESERVEES.find((s) => s.cle === cle)?.libelle || cle;
}

/**
 * Nettoie ce qui vient de Firestore : on ne garde que des cles connues.
 *
 * Une fiche peut porter n'importe quoi — un champ tape a la main dans la
 * console Firebase, une section supprimee depuis. Rendre une liste sure ici
 * evite d'avoir a s'en mefier partout ailleurs.
 */
export function normaliserSections(valeur) {
  if (!Array.isArray(valeur)) return [];
  return valeur.filter((cle) => typeof cle === "string" && estReservee(cle));
}

/** Pose l'autorisation dans le navigateur, a la connexion. */
export function poserSectionsAutorisees(valeur) {
  try {
    localStorage.setItem(CLE_LOCALE, JSON.stringify(normaliserSections(valeur)));
  } catch {
    // Un navigateur qui refuse le stockage local ne doit pas empecher la
    // connexion : on retombe simplement sur le menu par defaut.
  }
}

/** Les sections autorisees pour la personne connectee ici. */
export function sectionsAutorisees() {
  try {
    return normaliserSections(JSON.parse(localStorage.getItem(CLE_LOCALE) || "[]"));
  } catch {
    return [];
  }
}

/**
 * Faut-il cacher cette section a la personne connectee ?
 *
 * Une section qui n'est pas reservee n'est jamais cachee — c'est le cas de la
 * quasi-totalite du menu, et cette fonction doit repondre non sans avoir a
 * connaitre la liste complete des entrees.
 */
export function sectionMasquee(cle) {
  if (!estReservee(cle)) return false;
  return !sectionsAutorisees().includes(cle);
}

export default {
  CHAMP_FIRESTORE,
  CLE_LOCALE,
  SECTIONS_RESERVEES,
  estReservee,
  libelleSection,
  normaliserSections,
  poserSectionsAutorisees,
  sectionsAutorisees,
  sectionMasquee,
};
